import type { ScaleEvent } from '../domain/scales'

/**
 * Shortest plausible BOMBEO, in minutes: connecting, pumping and
 * disconnecting take longer, so a shorter one was recorded or declared wrong.
 */
export const MIN_BOMBEO_MINUTES = 15

/**
 * Fastest plausible pumping, in tonnes per hour: a BOMBEO whose tonnes ÷ hours
 * exceed it is far too short for what it moved.
 */
export const MAX_BOMBEO_TONNES_PER_HOUR = 1000

const MINUTE_MS = 60_000

/**
 * Why a BOMBEO's duration makes no sense: SHORT when under
 * `MIN_BOMBEO_MINUTES`, otherwise FAST when its rate exceeds
 * `MAX_BOMBEO_TONNES_PER_HOUR` (then with `tonnesPerHour`).
 */
export type DurationWarning = { reason: 'SHORT'; minutes: number } | { reason: 'FAST'; minutes: number; tonnesPerHour: number }

/** The duration warning of a BOMBEO with both a start and an end; null on others and on plausible ones. */
export const bombeoDurationWarning = (
  event: Pick<ScaleEvent, 'type' | 'startTime' | 'endTime'>,
  quantityT: number | null,
): DurationWarning | null => {
  if (event.type !== 'BOMBEO' || event.endTime === null) return null
  const minutes = (Date.parse(event.endTime) - Date.parse(event.startTime)) / MINUTE_MS
  if (minutes < MIN_BOMBEO_MINUTES) return { reason: 'SHORT', minutes }
  if (quantityT === null) return null
  const tonnesPerHour = quantityT / (minutes / 60)
  return tonnesPerHour > MAX_BOMBEO_TONNES_PER_HOUR ? { reason: 'FAST', minutes, tonnesPerHour } : null
}
